import React, { forwardRef, useRef } from "react";
import { Space, Text } from "react_reusable_components";
import {
  BarChart,
  Tooltip,
  YAxis,
  XAxis,
  Legend,
  Bar,
  ResponsiveContainer,
} from "recharts";
import variables from "../../../../../themes/base/styles/variables";
import useResponsive from "../../../../../core/hooks/useResponsive";
import html2canvas from "html2canvas";

export const GraphStyles = {
  labelBox: {
    display: variables.dFlex,
    fontWeight: variables.fontWeightNormal,
    fontSize: variables.fontSizeSmall,
    color: variables.charade,
    justifyContent: variables.spaceBetween,
    marginLeft: variables.marginLeftLegend,
  },
  customLegendParentStyles: {
    display: variables.dFlex,
    justifyContent: variables.justifyCenter,
  },
  customLegendChildStyles: {
    display: variables.dFlex,
    justifyContent: variables.spaceBetween,
  },
  labelBox2StylesMediumSize: {
    display: variables.dFlex,
    alignItems: variables.alignCenter,
  },
  customLegendStyle: (color) => ({
    width: variables.box,
    height: variables.box,
    backgroundColor: color, //taking color from props
  }),
  customLegendMobileSize: {
    flexDirection: variables.flexDirection,
    alignItems: variables.dFlex2,
    gap: variables.gapXS1,
    marginLeft: variables.mediumHeight,
  },
  labelBoxMobileSize: {
    fontSize: variables.fontSizeNormal,
  },
  labelBox2StylesMobileSize: {
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    gap: variables.gapXS1,
  },
};

const Graph = forwardRef(
  (
    {
      data,
      yAxisTicks,
      width,
      height,
      barSize,
      dataKey1,
      axisLine,
      dataKey2,
      fill1,
      fill2,
      style,
      dataKey3,
      dataKey4,
      dataKey5,
      dataKey6,
      dataKey7,
      linkLabel,
      colorBar1,
      colorBar2,
      colorBar3,
      colorBar4,
      selectedAuditType,
    },
    ref
  ) => {
    const chartRef = useRef(null);

    const downloadChartImage = () => {
      const node = chartRef.current;
      // Call the downloadChartImage function of the Graph component only if it is mounted
      try {
        if (node) {
          html2canvas(node).then((canvas) => {
            const link = document.createElement("a");
            link.download = linkLabel;
            link.href = canvas.toDataURL();
            link.click();
          });
        }
      } catch (error) {}
    };

    ref.current = {
      downloadChartImage,
    };

    const legends = [
      {
        key: dataKey2,
        color: fill2,
      },
      {
        key: dataKey3,
        color: variables.primaryBg,
      },
      {
        key: dataKey4,
        color: variables.cobaltBlue,
      },
      {
        key: dataKey5,
        color: variables.coral,
      },
      {
        key: dataKey6,
        color: fill1,
      },
      {
        key: dataKey7,
        color: variables.indigo,
      },
    ].filter(
      ({ key }) => !selectedAuditType || key.includes(selectedAuditType)
    );

    const disallowedKeys = [dataKey6, dataKey7];

    const CustomLegend = () => {
      const response = useResponsive();
      return (
        <Space
          style={{
            ...(response.isMd
              ? GraphStyles.customLegendParentStyles
              : GraphStyles.customLegendMobileSize),
            marginTop: variables.marginHalf,
            marginLeft: variables.marginLarge3,
          }}
        >
          {legends?.map(
            ({ key, color }, index) =>
              !disallowedKeys.includes(key) && (
                <>
                  <div
                    style={
                      response.isMd
                        ? GraphStyles.labelBox2StylesMediumSize
                        : GraphStyles.labelBox2StylesMobileSize
                    }
                  >
                    <div
                      key={`item-${index}`}
                      style={GraphStyles.customLegendStyle(color)}
                    />
                    <Text
                      style={
                        response.isMd
                          ? GraphStyles.labelBox
                          : GraphStyles.labelBoxMobileSize
                      }
                    >
                      {key}
                    </Text>
                  </div>
                </>
              )
          )}
        </Space>
      );
    };
    return (
      <div ref={chartRef} style={{ width }}>
        <ResponsiveContainer width={width} height={height}>
          <BarChart data={data} barSize={barSize} margin={{ left: -25 }}>
            <XAxis dataKey={dataKey1} axisLine={axisLine} />
            <YAxis axisLine={axisLine} tick={yAxisTicks} />
            <Tooltip cursor={{ fill: "transparent" }} />
            <Legend content={CustomLegend} />
            <Bar
              dataKey={dataKey3}
              fill={colorBar1}
              style={style}
              stackId="1"
            />
            <Bar
              dataKey={dataKey5}
              fill={colorBar2}
              style={style}
              stackId="1"
            />
            <Bar
              dataKey={dataKey6}
              fill={disallowedKeys.includes(dataKey6) ? "none" : fill1}
              style={style}
              stackId="1"
            />
            <Bar dataKey={dataKey2} fill={fill2} style={style} stackId="2" />
            <Bar
              dataKey={dataKey4}
              fill={colorBar3}
              style={style}
              stackId="2"
            />
            <Bar
              dataKey={dataKey7}
              fill={disallowedKeys.includes(dataKey7) ? "none" : colorBar4}
              style={style}
              stackId="2"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
);

export default Graph;
